import React from 'react';
import debounce from 'lodash/debounce';
import { network } from '@tencent/netcheck';
import './index.less';
import Dropdown from '@tencent/dui/lib/components/Dropdown';
import Menu from '@tencent/dui/lib/components/Menu';
import Divider from '@tencent/dui/lib/components/Divider';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import { InjectorService } from '@tencent/workbench';
import i18n from '@i18next/i18n';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { PlayActionEventsKeys, PlayActionFeature } from '@framework/workbench/ui/titlebar/features/play-action-feature';
import { MENU_NAME, MenuBarEventsKeys, MenuBarFeature } from '@framework/workbench/ui/titlebar/features/menu-bar-feature';

type Props = {
};

type States = {
    showToolTips: boolean;
    dropDownVisible: boolean;
    playContent: any[];
    active: boolean;
};

export default class PlayAction extends React.Component<Props, States> {
    private playAction: any;
    private playActionFeature: PlayActionFeature;
    private menuBarFeature: MenuBarFeature;
    private activeMenu: MENU_NAME | undefined;

    public constructor(props) {
        super(props);
        this.state = {
            showToolTips: true,
            dropDownVisible: false,
            playContent: [],
            active: false,
        };
    }

    public componentDidMount() {
        const updatePlayContent = debounce(() => {
            const playContent = this.playActionFeature.generatePlayContent();
            this.setState({
                playContent,
            });
        });
        network.changeEnd(updatePlayContent);
        this.playActionFeature = InjectorService.get(PlayActionFeature);
        this.playActionFeature.event.on(PlayActionEventsKeys.USER_CHANGE, updatePlayContent);
        this.playActionFeature.event.on(PlayActionEventsKeys.PLAY_ACTION_ACTIVE_CHANGE, (active) => {
            this.setState({
                active,
            });
        });
        this.menuBarFeature = InjectorService.get(MenuBarFeature);
        this.menuBarFeature.event.on(MenuBarEventsKeys.ACTIVE_BTN_CHANGE, (activeMenu) => {
            this.activeMenu = activeMenu;
            const isShow = activeMenu === MENU_NAME.PLAY;
            this.playIconChange(isShow);
        });

        const playContent = this.playActionFeature.generatePlayContent();
        this.setState({
            playContent,
        });
    }

    public render() {
        return (
            <React.Fragment>
                {this.renderActions()}
                <div id='live-show-btn'></div>
                <div id='live-invite-btn'></div>
                <div id='mobile-control-btn'></div>
            </React.Fragment>
        );
    }

    private renderActions = () => {
        const { active } = this.state;
        const dropContent = this.getActionContent();
        const disabled = this.activeMenu === MENU_NAME.DISABLED;
        const style = disabled ? {
            opacity: 0.3,
        } : {};

        return (
            <Dropdown
                className="pc-header-play"
                alignment='right'
                dropContent={dropContent}
                dropContentStyle={{ minWidth: 180 }}
                dropContentClassName='pc-header-play-dropdown'
                onVisibleChange={this.handleVisibleChange}
                visible={this.state.dropDownVisible && !disabled}
                animated={false}
            >
                <Tooltip
                    placement="bottom"
                    title={i18n.t('演示')}
                    containerOffsetX={8}
                    containerOffsetY={1}
                    disabled={!this.state.showToolTips || disabled}
                >
                    <div
                        className="play-container"
                        ref={ref => this.playAction = ref}
                        onMouseOver={this.playMouseOver}
                        onClick={this.playClick}
                        style={style}
                    >
                        <div className={`play-icon ${active ? 'play-icon-active' : ''}`} />
                    </div>
                </Tooltip>
            </Dropdown>
        );
    };

    private handleVisibleChange = (visible) => {
        const activeMenu = visible ? MENU_NAME.PLAY : undefined;
        this.menuBarFeature.toggleAutoExpandDropdownStatus(visible);
        this.menuBarFeature.updateActiveMenu(activeMenu);
    };

    private getActionContent() {
        const { playContent } = this.state;
        return (playContent.length !== 1)
            ? <Menu selectable={false} onClick={this.handleClick}>
                {playContent.map((item, index) => {
                    if (item.showDivider
                        || (item.showDivider
                            ?? (index === 2 && playContent.length === 3)
                        )) {
                        return (
                            <React.Fragment key={`container-divider${index}`}>
                                <Divider key={`divider${index}`}/>
                                {this.getItemContent(item)}
                            </React.Fragment>
                        );
                    }
                    return this.getItemContent(item);
                })}
            </Menu>
            : null;
    }

    private handleClick = (id: string) => {
        const { playContent } = this.state;

        tdw({
            opername: 'doc_menubar',
            module: 'play_button',
            action: 'click',
        });

        playContent.forEach((item) => {
            if (item.id === id) {
                item.onClick();
            }
        });
    };

    private getItemContent = (item) => {
        const { id, title, subTitle, content, style, className, icon } = item;

        if (!content) {
            return <Menu.Item
                key={id}
                id={id}
                icon={icon}
                style={style}
                className={`menu-item ${className}`}>
                <p>{title}</p>
                {subTitle && <p className={'sub-title'}>{subTitle}</p>}
            </Menu.Item>;
        }
        return <Menu.Item
            key={id}
            id={id}
            icon={icon}
            style={style}
            className={`menu-item ${className}`}>
            {content}
        </Menu.Item>;
    };

    private playIconChange = (visible: boolean) => {
        this.setState({
            showToolTips: !visible,
            dropDownVisible: visible,
        });
        const playContainer = document.querySelector('.play-container');
        if (playContainer) {
            if (visible) {
                playContainer.classList.add('hover');
            } else {
                playContainer.classList.remove('hover');
            }
        }
    };

    private playMouseOver = (e) => {
        this.menuBarFeature.mouseEnter(MENU_NAME.PLAY);
        e.stopPropagation();
    };

    private playClick = () => {
        if (this.activeMenu === MENU_NAME.DISABLED) {
            return;
        }
        // 会议打点上报 https://docs.qq.com/sheet/BYeyFa0J8NYX4wVI1f2dm7HZ18QPLK0Rx8si1?tab=knagjw
        tdw({
            opername: 'doc_meet_web',
            module: 'doc_to_meet',
            action: 'begin_presentation_view',
        });
        const { playContent } = this.state;
        if (playContent?.length === 1) {
            // 兼容只有一个的情况
            tdw({
                opername: 'doc_menubar',
                module: 'play_button',
                action: 'click',
            });
            playContent[0].onClick();
            return;
        }
        this.setState({
            dropDownVisible: !this.state.dropDownVisible,
        });
    };
}
